/**
 * Web Push Service — UAT-3B
 *
 * Bootstraps Firebase JS SDK from /api/firebase/web-config (NEVER from
 * .env — the config is fetched at runtime so admins can rotate without
 * a frontend redeploy).
 *
 * Registers an FCM token with the backend on first call and listens for
 * foreground push messages → re-emits them as the same
 * `snappipit:notification` window CustomEvent that the in-app drawer is
 * already listening to. Zero extra wiring on the consumer side.
 *
 * Idempotent: safe to call multiple times. No-ops if:
 *   • Browser doesn't support service workers / push.
 *   • Backend reports `configured: false` (Firebase not yet set up).
 *   • User has denied notification permission.
 */
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import axios from 'axios';
import { deviceTokensAPI } from '../lib/api';
import { initNotificationSound, playNotificationSound } from './notificationSound';

const SW_PATH = '/firebase-messaging-sw.js';
let _initPromise = null;
let _registeredToken = null;

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

async function fetchWebConfig() {
  // Use the same axios instance from lib/api — but we don't import that here
  // to keep the SW registration self-contained.
  const token = localStorage.getItem('snappipit_token');
  if (!token) return null;
  try {
    const res = await axios.get(`${API_BASE}/api/firebase/web-config`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch {
    return null;
  }
}

async function pushFeatureSupported() {
  if (typeof window === 'undefined') return false;
  if (!('Notification' in window)) return false;
  if (!('serviceWorker' in navigator)) return false;
  try {
    return await isSupported();
  } catch {
    return false;
  }
}

async function ensureServiceWorker(cfg) {
  if (!('serviceWorker' in navigator)) return null;
  try {
    // Pass the public Firebase config to the SW via URL params so it can
    // bootstrap without baking any keys into the static file. Keys rotate
    // via the admin UI; SW URL changes automatically.
    const params = new URLSearchParams({
      config: JSON.stringify({
        apiKey: cfg.apiKey,
        projectId: cfg.projectId,
        messagingSenderId: cfg.messagingSenderId,
        appId: cfg.appId,
      }),
    });
    return await navigator.serviceWorker.register(`${SW_PATH}?${params.toString()}`);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[webPush] SW registration failed', err);
    return null;
  }
}

async function _doInit() {
  initNotificationSound();
  if (!(await pushFeatureSupported())) {
    return { status: 'unsupported' };
  }

  const cfg = await fetchWebConfig();
  if (!cfg || !cfg.configured) {
    return { status: 'not-configured' };
  }

  // Permission flow — only PROMPT here, don't auto-request. The caller
  // (e.g. NotificationBell first-open or AdminFirebase test) decides
  // whether to display the prompt.
  if (Notification.permission === 'denied') {
    return { status: 'permission-denied' };
  }

  let firebaseApp;
  if (getApps().length === 0) {
    firebaseApp = initializeApp({
      apiKey: cfg.apiKey,
      projectId: cfg.projectId,
      messagingSenderId: cfg.messagingSenderId,
      appId: cfg.appId,
    });
  } else {
    [firebaseApp] = getApps();
  }

  const swReg = await ensureServiceWorker(cfg);
  if (!swReg) return { status: 'sw-failed' };

  // Ask for permission if still default.
  if (Notification.permission === 'default') {
    const result = await Notification.requestPermission();
    if (result !== 'granted') {
      return { status: 'permission-denied' };
    }
  }

  const messaging = getMessaging(firebaseApp);
  let fcmToken;
  try {
    fcmToken = await getToken(messaging, {
      vapidKey: cfg.vapidKey,
      serviceWorkerRegistration: swReg,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[webPush] getToken failed', err);
    return { status: 'token-failed', error: err.message };
  }

  if (!fcmToken) return { status: 'token-empty' };
  _registeredToken = fcmToken;

  try {
    await deviceTokensAPI.register({
      token: fcmToken,
      platform: 'web',
      device_model: navigator.userAgent.slice(0, 200),
    });
  } catch {
    // backend may be down — surface but don't crash
  }

  // Foreground listener — fold message into the existing in-app pipeline.
  onMessage(messaging, (payload) => {
    if (typeof window === 'undefined') return;
    // Server-side build of the notification doc populated data.* with id,
    // event_type, category; notification.title/body via the FCM payload.
    const n = {
      id: payload.data?.notification_id || `local-${Date.now()}`,
      user_id: 'self',
      event_type: payload.data?.event_type || 'unknown',
      category: payload.data?.category || 'general',
      title: payload.notification?.title || payload.data?.title || '',
      body: payload.notification?.body || payload.data?.body || '',
      link: payload.data?.link || null,
      data: payload.data || {},
      read: false,
      created_at: new Date().toISOString(),
    };
    playNotificationSound(n.id || payload.messageId || n.event_type);
    window.dispatchEvent(new CustomEvent('snappipit:notification', { detail: n }));
    window.dispatchEvent(new CustomEvent('snappipit:notification_unread_changed', {
      detail: { category: n.category },
    }));
    // P2: update the PWA app-icon badge from the push payload if present.
    try {
      const count = parseInt(payload.data?.unread_count, 10);
      if (!Number.isNaN(count) && navigator.setAppBadge) navigator.setAppBadge(count);
    } catch {
      /* unsupported — ignore */
    }
  });

  return { status: 'registered', token: fcmToken };
}

/**
 * Initialize web push. Returns the registration status.
 * Safe to call multiple times — returns the same promise on subsequent calls.
 */
export function initWebPush() {
  if (!_initPromise) _initPromise = _doInit();
  return _initPromise;
}

/**
 * Reset cached init promise. Used after logout so a new user gets a
 * fresh token registration.
 */
export function resetWebPush() {
  _initPromise = null;
  _registeredToken = null;
}

/**
 * Best-effort token unregister — called on logout. Doesn't wait.
 */
export function unregisterWebPushToken() {
  if (!_registeredToken) return;
  const token = _registeredToken;
  resetWebPush();
  deviceTokensAPI.unregister(token).catch(() => {});
}

export function getRegisteredToken() {
  return _registeredToken;
}
